import {
  FhirHTTPCodes,
  FhirStatus,
  StatusAlert,
  StatusCode,
  StatusIcon,
  Title,
} from "@fyrstain/fhir-front-library";
import i18n from "i18next";
import { FunctionComponent, useCallback, useState } from "react";
import { Card, Form, Table } from "react-bootstrap";
import JsonViewer from '../../components/JsonViewer/JsonViewer';
import { useNavigate, useParams } from "react-router-dom";
import PandoraPage from "../../components/PandoraPage/PandoraPage";
import styles from "./auditEventExchangeDetails.module.css";
import { toPublicUrl } from "src/services/PublicUrl";
import { getAuditEventById, changeServer } from "src/services/AuditEventDataService";
import { useEnvironment } from "src/context/EnvironmentContext";
import { useOnMount } from "src/hooks/useOnMount";

const AuditEventExchangeDetails: FunctionComponent = () => {
  /////////////////////////////////////
  //             State               //
  /////////////////////////////////////

  const [loading, setLoading] = useState<boolean>(false);
  const { exchangeId } = useParams<{ exchangeId: string }>();

  const [method, setMethod] = useState("");
  const [path, setPath] = useState("");
  const [sender, setSender] = useState("");
  const [statusResponse, setStatusResponse] = useState("");
  const [partHeaderRequest, setPartHeaderRequest] = useState([] as any[]);
  const [partHeaderResponse, setPartHeaderResponse] = useState([] as any[]);
  const [bodyRequest, setBodyRequest] = useState("N/A");
  const [bodyResponse, setBodyResponse] = useState("N/A");
  const [recorded, setRecorded] = useState("");
  const [validationReportLink, setValidationReportLink] = useState("");
  const [auditEvent, setAuditEvent] = useState<any>();

  const { currentEnvironment } = useEnvironment();

  /////////////////////////////////////
  //             Actions             //
  /////////////////////////////////////

  const navigate = useNavigate();

  useOnMount(() => {
    changeServer(currentEnvironment?.messageUrl ?? process.env.REACT_APP_FHIR_URL_AUDIT ?? "fhir");
    void loadPage();
  });

  const onError = useCallback((error?: unknown) => {
    navigate("/Error", { state: { error } });
  }, [navigate]);

  /**
   * Load exchange details from the back to populate the table.
   */
  async function loadExchangeDetails() {
    try {
      const auditEvent = await getAuditEventById(exchangeId ?? "");

      if (!auditEvent) {
        onError();
        return;
      }

      setAuditEvent(auditEvent);
      setMethod(getMethod(auditEvent));
      setPath(getPath(auditEvent));
      setSender(getSender(auditEvent));
      setStatusResponse(getStatus(auditEvent));
      setPartHeaderRequest(getPartHeaderRequest(auditEvent));
      setPartHeaderResponse(getPartHeaderResponse(auditEvent));
      setBodyRequest(getBodyRequest(auditEvent));
      setBodyResponse(getBodyResponse(auditEvent));
      setRecorded(getRecorded(auditEvent));
      setValidationReportLink(getValidationReportLink(auditEvent));
    } catch (error) {
      onError();
    }
  }

  async function loadPage() {
    setLoading(true);
    await loadExchangeDetails();
    setLoading(false);
  }

  /////////////////////////////////////
  //            Mapping              //
  /////////////////////////////////////

  function getMethod(auditEvent: any): string {
    for (const entity of auditEvent?.entity ?? []){
      for (const detail of entity?.detail ?? []){
        const label = 
          detail?.type?.text ??
          detail?.type?.coding?.[0]?.display ??
          detail?.type?.coding?.[0]?.code;

        if (
          label &&
          ["method", "http method", "request method", "verb"].includes(
            String(label).toLowerCase(),
          )
        ){
          return detail?.valueString ??
            detail?.valueCodeableConcept?.text ??
            detail?.valueCodeableConcept?.coding?.[0]?.display ??
            detail?.valueCodeableConcept?.coding?.[0]?.code ??
            auditEvent?.action ??
            i18n.t("general.notavailable");
        }
      }
    }
    if (auditEvent?.action === "C") return "POST";
    if (auditEvent?.action === "R") return "GET";
    if (auditEvent?.action === "U") return "PUT";
    if (auditEvent?.action === "D") return "DELETE";
    if (auditEvent?.action === "E") return "POST";
    
    return auditEvent?.action ? auditEvent.action : i18n.t("general.notavailable");
  }

  function getPath(auditEvent: any): string {
    for (const entity of auditEvent?.entity ?? []) {
      for (const detail of entity?.detail ?? []) {
        const label =
          detail?.type?.text ??
          detail?.type?.coding?.[0]?.display ??
          detail?.type?.coding?.[0]?.code;

        if (label && String(label).toLowerCase() === "route") {
          return (
            detail?.valueString ??
            detail?.valueCodeableConcept?.text ??
            detail?.valueCodeableConcept?.coding?.[0]?.display ??
            detail?.valueCodeableConcept?.coding?.[0]?.code ??
            i18n.t("general.notavailable")
          );
        }
      }
    if (entity?.query) {
      try {
        const decodedQuery = atob(entity.query);
        if (decodedQuery.startsWith("/")) {
          return decodedQuery;
        }
        try {
          const url = new URL(decodedQuery, "http://dummy");
          return url.pathname || i18n.t("general.notavailable");
        } catch {
          return decodedQuery;
        }
      } catch {
        return entity.query;
      }
    }
  }
  const queryEntity = auditEvent?.entity?.find(
      (e: any) =>
        e?.what?.display === "query" ||
        e?.description === "query" ||
        e?.query,
    );

    return queryEntity?.description
      ? queryEntity.description
      : queryEntity?.query
        ? queryEntity.query
        : queryEntity?.what?.reference
          ? queryEntity.what.reference
          : queryEntity?.what?.display
            ? queryEntity.what.display
            : auditEvent?.entity?.[0]?.description
              ? auditEvent.entity[0].description
              : auditEvent?.entity?.[0]?.what?.reference
                ? auditEvent.entity[0].what.reference
                : auditEvent?.entity?.[0]?.what?.display
                  ? auditEvent.entity[0].what.display
                  : i18n.t("general.notavailable");
  }

  function getSender(auditEvent: any): string {
    const requestorAgent = auditEvent?.agent?.find((a: any) => a.requestor);
    const agent = requestorAgent ?? auditEvent?.agent?.[0];

    return agentLabel(agent);
  }

  function getStatus(auditEvent: any): string {
    const outcome = auditEvent?.outcome;
    const code =
      outcome?.code?.code ??
      outcome?.code?.display ??
      outcome?.code ??
      outcome ??
      auditEvent?.outcomeDesc;

    switch (String(code).toLowerCase()) {
      case "0":
      case "success":
      case "completed":
        return "completed";
      case "4":
      case "minor failure":
      case "minor-failure":
        return "warning";
      case "8":
      case "serious failure":
      case "serious-failure":
      case "12":
      case "major failure":
      case "major-failure":
        return "failed";
      default:
        return "unknownResponse";
    }
  }

  function getRecorded(auditEvent: any): string {
    return auditEvent?.recorded ? auditEvent.recorded : i18n.t("general.notavailable");
  }

  function codingLabel(concept: any): string {
    const coding = concept?.coding?.[0] ?? concept;
    return coding?.display ?? coding?.code ?? concept?.text ?? i18n.t("general.notavailable");
  }

  function agentLabel(agent: any): string {
    return agent?.who?.identifier?.value
      ?? agent?.who?.display
      ?? agent?.who?.reference
      ?? agent?.name
      ?? i18n.t("general.notavailable");
  }

  function detailValue(auditEvent: any, type: string): string {
    for (const entity of auditEvent?.entity ?? []) {
      const detail = entity?.detail?.find((item: any) => item?.type === type);
      if (detail) {
        return detail.valueString
          ?? detail.valueCodeableConcept?.text
          ?? codingLabel(detail.valueCodeableConcept);
      }
    }
    return i18n.t("general.notavailable");
  }

  function interactionLabel(auditEvent: any): string {
    return auditEvent?.subtype?.map((subtype: any) => codingLabel(subtype)).join(", ")
      || detailValue(auditEvent, "interaction");
  }

  function sourceLabel(auditEvent: any): string {
    const observer = auditEvent?.source?.observer;
    const containedId = observer?.reference?.replace("#", "");
    const containedDevice = auditEvent?.contained?.find((resource: any) => resource.id === containedId);
    const deviceName = containedDevice?.deviceName?.[0]?.name;
    return [auditEvent?.source?.site, deviceName ?? observer?.display ?? observer?.reference]
      .filter(Boolean)
      .join(" / ") || i18n.t("general.notavailable");
  }

  function entityIdentifier(entity: any): string {
    return entity?.what?.identifier?.value
      ?? entity?.what?.reference
      ?? entity?.what?.display
      ?? i18n.t("general.notavailable");
  }

  function entityDetails(entity: any): string {
    const details = (entity?.detail ?? []).map((detail: any) => {
      const value = detail.valueString
        ?? detail.valueCodeableConcept?.text
        ?? codingLabel(detail.valueCodeableConcept);
      return `${detail.type ?? i18n.t("label.details")}: ${value}`;
    });
    if (entity?.query) {
      try {
        details.push(`${i18n.t("label.query")}: ${atob(entity.query)}`);
      } catch {
        details.push(`${i18n.t("label.query")}: ${entity.query}`);
      }
    }
    return details.join(" · ") || i18n.t("general.notavailable");
  }

  function getPartHeaderRequest(auditEvent: any) {
    const result: any[] = [];

    auditEvent?.entity?.forEach((e: any) => {
      if (
        e?.what?.display === "requestHeader" ||
        e?.description === "requestHeader"
      ) {
        e?.detail?.forEach((part: any) => {
          result.push({
            name:
              part?.type?.text ??
              part?.type?.coding?.[0]?.display ??
              part?.type?.coding?.[0]?.code ??
              i18n.t("general.notavailable"),
            valueString:
              part?.valueString ??
              part?.valueCodeableConcept?.text ??
              part?.valueCodeableConcept?.coding?.[0]?.display ??
              part?.valueCodeableConcept?.coding?.[0]?.code ??
              i18n.t("general.notavailable"),
          });
        });
      }
    });

    return result;
  }

  function getPartHeaderResponse(auditEvent: any) {
    const result: any[] = [];

    auditEvent?.entity?.forEach((e: any) => {
      if (
        e?.what?.display === "responseHeader" ||
        e?.description === "responseHeader"
      ) {
        e?.detail?.forEach((part: any) => {
          result.push({
            name:
              part?.type?.text ??
              part?.type?.coding?.[0]?.display ??
              part?.type?.coding?.[0]?.code ??
              i18n.t("general.notavailable"),
            valueString:
              part?.valueString ??
              part?.valueCodeableConcept?.text ??
              part?.valueCodeableConcept?.coding?.[0]?.display ??
              part?.valueCodeableConcept?.coding?.[0]?.code ??
              i18n.t("general.notavailable"),
          });
        });
      }
    });

    return result;
  }

  function getBodyRequest(auditEvent: any): string {
    const bodyRequestEntity = auditEvent?.entity?.find(
      (e: any) => 
        e?.what?.display === "requestBody" ||
        e?.description === "requestBody",
    );

    return bodyRequestEntity?.detail?.[0]?.valueString
      ? bodyRequestEntity.detail[0].valueString
      : bodyRequestEntity?.detail?.[0]?.valueCodeableConcept?.text
        ? bodyRequestEntity.detail[0].valueCodeableConcept.text
        : bodyRequestEntity?.detail?.[0]?.valueCodeableConcept?.coding?.[0]
            ?.display
          ? bodyRequestEntity.detail[0].valueCodeableConcept.coding[0].display
          : "N/A";
  }

  function getBodyResponse(auditEvent: any): string {
    const bodyResponseEntity = auditEvent?.entity?.find(
      (e: any) => 
        e?.what?.display === "responseBody" ||
        e?.description === "responseBody",
    );

    return bodyResponseEntity?.detail?.[0]?.valueString
      ? bodyResponseEntity.detail[0].valueString
      : bodyResponseEntity?.detail?.[0]?.valueCodeableConcept?.text
        ? bodyResponseEntity.detail[0].valueCodeableConcept.text
        : bodyResponseEntity?.detail?.[0]?.valueCodeableConcept?.coding?.[0]
            ?.display
          ? bodyResponseEntity.detail[0].valueCodeableConcept.coding[0].display
          : "N/A";
  }

  function getValidationReportLink(auditEvent: any): string {
    const validationReportEntity = auditEvent?.entity?.find(
      (e: any) =>
        e?.what?.display === "validationReport" ||
        e?.description === "validationReport" ||
        e?.what?.reference?.startsWith("ValidationReport/") ||
        e?.what?.identifier?.value?.startsWith("Bundle/"),
    );

    const reportReference = validationReportEntity?.what?.reference
      ?? validationReportEntity?.what?.identifier?.value;
    return reportReference
      ? `/ValidationReport/${reportReference.split("/").pop()}`
      : "";
  }

  function parseJson(response: string) {
    try {
      return JSON.parse(response);
    } catch (ex) {
      return {};
    }
  }

  /////////////////////////////////////
  //          Page Content           //
  /////////////////////////////////////

  return (
    <PandoraPage titleKey="title.auditeventexchangedetails" loading={loading} needsLogin={true}>
      <>
        <div className="section">
          <Card className={styles.card}>
            <Card.Header>
              <Title level={2} content={i18n.t("title.generalinformations")} />
            </Card.Header>
            <Card.Body className="cardBody">
              <div>
                <div className="spaceBetweenContainer">
                  <div className="mediumContainer">
                    <div>
                      <Form.Label className={styles.label}>
                        <strong>{i18n.t("label.id")} :</strong>
                      </Form.Label>
                      <Form.Text>{exchangeId}</Form.Text>
                    </div>
                    <div>
                      <Form.Label className={styles.label}>
                        <strong>{i18n.t("label.action")} :</strong>
                      </Form.Label>
                      <Form.Text>{method}</Form.Text>
                    </div>
                    <div>
                      <Form.Label className={styles.label}>
                        <strong>{i18n.t("label.date")} :</strong>
                      </Form.Label>
                      <Form.Text>{recorded}</Form.Text>
                    </div>
                  </div>

                  <div className="mediumContainer">
                    <div>
                      <Form.Label className={styles.label}>
                        <strong>{i18n.t("label.sender")} :</strong>
                      </Form.Label>
                      <Form.Text>{sender}</Form.Text>
                    </div>
                    <div>
                      <Form.Label className={styles.label}>
                        <strong>{i18n.t("title.request")} :</strong>
                      </Form.Label>
                      <Form.Text>{path}</Form.Text>
                    </div>
                  </div>
                </div>

                {validationReportLink && (
                  <div>
                    <Form.Label className={styles.label}>
                      <strong>{i18n.t("title.validationreport")} :</strong>
                    </Form.Label>
                    <Form.Text>
                      <a href={toPublicUrl(validationReportLink)}>{i18n.t("button.viewreport")}</a>
                    </Form.Text>
                  </div>
                )}
              </div>

              <div>
                <StatusAlert
                  status={FhirStatus[statusResponse as keyof typeof FhirStatus]}
                >
                  <div>
                    <div>
                      <StatusIcon
                        status={
                          FhirStatus[statusResponse as keyof typeof FhirStatus]
                        }
                      />
                      <StatusCode
                        status={statusResponse}
                        statusCode={
                          FhirHTTPCodes[
                            statusResponse as keyof typeof FhirHTTPCodes
                          ]
                        }
                      />
                    </div>
                  </div>
                </StatusAlert>
              </div>
            </Card.Body>
          </Card>
        </div>

        {auditEvent && (
          <div className="section">
            <Card className={styles.card}>
              <Card.Header>
                <Title level={2} content={i18n.t("title.auditdetails")} />
              </Card.Header>
              <Card.Body className="cardBody">
                <div className="spaceBetweenContainer">
                  <div className="mediumContainer">
                    <div>
                      <Form.Label className={styles.label}><strong>{i18n.t("label.interaction")} :</strong></Form.Label>
                      <Form.Text>{interactionLabel(auditEvent)}</Form.Text>
                    </div>
                    <div>
                      <Form.Label className={styles.label}><strong>{i18n.t("label.httpstatus")} :</strong></Form.Label>
                      <Form.Text>{detailValue(auditEvent, "status")}</Form.Text>
                    </div>
                    <div>
                      <Form.Label className={styles.label}><strong>{i18n.t("label.result")} :</strong></Form.Label>
                      <Form.Text>{auditEvent.outcomeDesc ?? auditEvent.outcome ?? i18n.t("general.notavailable")}</Form.Text>
                    </div>
                  </div>
                  <div className="mediumContainer">
                    <div>
                      <Form.Label className={styles.label}><strong>{i18n.t("label.atnasource")} :</strong></Form.Label>
                      <Form.Text>{sourceLabel(auditEvent)}</Form.Text>
                    </div>
                    <div>
                      <Form.Label className={styles.label}><strong>{i18n.t("label.transactionid")} :</strong></Form.Label>
                      <Form.Text>{auditEvent.entity?.find((entity: any) => entity.type?.code === "XrequestId")?.what?.identifier?.value ?? i18n.t("general.notavailable")}</Form.Text>
                    </div>
                  </div>
                </div>

                <Table striped bordered hover responsive className={styles.auditTable}>
                  <thead>
                    <tr className={styles.headerTableContainer}>
                      <th colSpan={4}><Title level={3} content={i18n.t("title.atnaagents")} /></th>
                    </tr>
                    <tr><th>{i18n.t("label.role")}</th><th>{i18n.t("label.identity")}</th><th>{i18n.t("label.requestor")}</th><th>{i18n.t("label.network")}</th></tr>
                  </thead>
                  <tbody>
                    {(auditEvent.agent ?? []).map((agent: any, index: number) => (
                      <tr key={index}>
                        <td>{codingLabel(agent.type)}</td>
                        <td>{agentLabel(agent)}</td>
                        <td>{agent.requestor ? i18n.t("general.yes") : i18n.t("general.no")}</td>
                        <td>{agent.network?.address ?? i18n.t("general.notavailable")}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Table striped bordered hover responsive className={styles.auditTable}>
                  <thead>
                    <tr className={styles.headerTableContainer}>
                      <th colSpan={4}><Title level={3} content={i18n.t("title.auditedentities")} /></th>
                    </tr>
                    <tr><th>{i18n.t("label.type")}</th><th>{i18n.t("label.identifier")}</th><th>{i18n.t("label.role")}</th><th>{i18n.t("label.details")}</th></tr>
                  </thead>
                  <tbody>
                    {(auditEvent.entity ?? []).map((entity: any, index: number) => (
                      <tr key={index}>
                        <td>{entity.description ?? codingLabel(entity.type)}</td>
                        <td>{entityIdentifier(entity)}</td>
                        <td>{codingLabel(entity.role)}</td>
                        <td>{entityDetails(entity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </div>
        )}

        <div className="section">
          <Card className={styles.card}>
            <Card.Header>
              <Title level={2} content={i18n.t("title.headers")} />
            </Card.Header>
            <Card.Body className="cardBody">
              <div className="spaceBetweenContainerStart">
                <Table striped bordered hover>
                  <thead>
                    <tr className={styles.headerTableContainer}>
                      <th className={styles.tableTitle} colSpan={2}>
                        <Title level={3} content={i18n.t("title.request")} />
                      </th>
                    </tr>
                    <tr>
                      <th>{i18n.t("label.name")}</th>
                      <th>{i18n.t("label.value")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partHeaderRequest.map((p, index) => (
                      <tr key={index}>
                        <td>{p.name}</td>
                        <td>{p.valueString}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Table striped bordered hover className={styles.table}>
                  <thead>
                    <tr className={styles.headerTableContainer}>
                      <th className={styles.tableTitle} colSpan={2}>
                        <Title level={3} content={i18n.t("title.response")} />
                      </th>
                    </tr>
                    <tr>
                      <th>{i18n.t("label.name")}</th>
                      <th>{i18n.t("label.value")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partHeaderResponse.map((p, index) => (
                      <tr key={index}>
                        <td>{p.name}</td>
                        <td>{p.valueString}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="section">
          <Card>
            <Card.Header>
              <Title level={2} content={i18n.t("title.body")} />
            </Card.Header>
            <Card.Body>
              <Card>
                <Card.Header>
                  <Title level={3} content={i18n.t("title.request")} />
                </Card.Header>
                {bodyRequest === "N/A" && <Card.Body>{i18n.t("general.notavailable")}</Card.Body>}
                {bodyRequest !== "N/A" && (
                  <Card.Body>
                    <JsonViewer src={parseJson(bodyRequest)} />
                  </Card.Body>
                )}
              </Card>

              <div className={styles.responseSection}>
                <Card className={styles.responseCard}>
                  <Card.Header>
                    <Title level={3} content={i18n.t("title.response")} />
                  </Card.Header>
                  {bodyResponse === "N/A" && (
                    <Card.Body>{i18n.t("general.notavailable")}</Card.Body>
                  )}
                  {bodyResponse !== "N/A" && (
                    <Card.Body>
                      <JsonViewer src={parseJson(bodyResponse)} />
                    </Card.Body>
                  )}
                </Card>
              </div>
            </Card.Body>
          </Card>
        </div>
      </>
    </PandoraPage>
  );
};

export default AuditEventExchangeDetails;
